import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import ViewViewer from "./ViewViewer";

const mockAuthFetch = jest.fn();
jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("../components/viewGrid", () => {
  const { toServerFilters } = jest.requireActual(
    "../components/viewGrid/serverFilters",
  );
  return {
    ViewGridSkeleton: () => <div>loading</div>,
    ViewGridNew: ({ data, page, onPageChange, onColumnFilterChange }: any) => (
      <div>
        <div>Page {page + 1}</div>
        <div>Total {data.totalCount}</div>
        {data.rows.map((row: any) => (
          <div key={row.id}>{row.first_name}</div>
        ))}
        <button onClick={() => onPageChange(1)}>Next page</button>
        <button
          onClick={() =>
            onColumnFilterChange?.(
              toServerFilters({
                items: [
                  { field: "first_name", operator: "contains", value: "Lena" },
                ],
              }),
            )
          }
        >
          Filter Lena
        </button>
        <button onClick={() => onColumnFilterChange?.([])}>Clear filter</button>
      </div>
    ),
  };
});

const people = Array.from({ length: 26 }, (_, i) => ({
  id: `person-${i}`,
  first_name: i === 25 ? "Lena" : `Person ${i}`,
}));
beforeEach(() => {
  mockAuthFetch.mockReset();
  mockAuthFetch.mockImplementation(async (url, _description, options) => {
    if (url.endsWith("/full"))
      return { id: "view-1", name: "People", focus_table_id: "table-1" };
    if (url.endsWith("/tables/table-1"))
      return { primary_key_column: "person_key" };
    if (url.includes("/query")) {
      const request = JSON.parse(options.body);
      const matching = people.filter((person) =>
        (request.filters || []).every((filter: any) =>
          filter.field === "first_name"
            ? person.first_name.includes(filter.value)
            : filter.field === "person_key"
              ? person.id === filter.value
              : true,
        ),
      );
      return {
        rows: matching.slice(
          (request.page - 1) * request.page_size,
          request.page * request.page_size,
        ),
        columns: [],
        total_count: matching.length,
      };
    }
    throw new Error(`Unexpected path ${url}`);
  });
});

function openViewer(row = "") {
  const client = new QueryClient({
    defaultOptions: { queries: { retry: false } },
  });
  render(
    <QueryClientProvider client={client}>
      <MemoryRouter initialEntries={[`/views/view-1${row}`]}>
        <Routes>
          <Route path="/views/:id" element={<ViewViewer />} />
        </Routes>
      </MemoryRouter>
    </QueryClientProvider>,
  );
  return client;
}
const activeParams = (client: QueryClient) => {
  const query = client
    .getQueryCache()
    .getAll()
    .find((item) => item.isActive() && item.queryKey.includes("query"))!;
  return query.queryKey[query.queryKey.length - 1] as {
    page: number;
    filters: unknown[];
  };
};
const queryRequests = () =>
  mockAuthFetch.mock.calls
    .filter(([url]) => url.includes("/query"))
    .map(([, , options]) => JSON.parse(options.body));

it("filters all records through authFetch, finds a page-two match and resets the page", async () => {
  const client = openViewer();
  await screen.findByText("Person 0");
  expect(screen.queryByText("Lena")).toBeNull();
  fireEvent.click(screen.getByText("Filter Lena"));
  await screen.findByText("Total 1");
  expect(screen.getByText("Lena")).toBeTruthy();
  fireEvent.click(screen.getByText("Clear filter"));
  await screen.findByText("Total 26");
  fireEvent.click(screen.getByText("Next page"));
  await screen.findByText("Lena");
  fireEvent.click(screen.getByText("Filter Lena"));
  await screen.findByText("Total 1");
  expect(screen.getByText("Page 1")).toBeTruthy();
  expect(screen.getByText("Lena")).toBeTruthy();
  expect(activeParams(client)).toEqual(
    expect.objectContaining({
      page: 1,
      filters: [{ field: "first_name", operator: "contains", value: "Lena" }],
    }),
  );
  fireEvent.click(screen.getByText("Clear filter"));
  await screen.findByText("Total 26");
  expect(screen.queryByText("Lena")).toBeNull();
});

it("keeps the focused-row predicate when applying and clearing a column filter", async () => {
  const client = openViewer("?row=person-25");
  await screen.findByText("Lena");
  fireEvent.click(screen.getByText("Filter Lena"));
  await waitFor(() =>
    expect(queryRequests().slice(-1)[0].filters).toHaveLength(2),
  );
  expect(queryRequests().slice(-1)[0].filters).toEqual([
    { field: "person_key", operator: "eq", value: "person-25" },
    { field: "first_name", operator: "contains", value: "Lena" },
  ]);
  fireEvent.click(await screen.findByText("Clear filter"));
  await waitFor(() => expect(activeParams(client).filters).toHaveLength(1));
  expect(activeParams(client).filters).toEqual([
    { field: "person_key", operator: "eq", value: "person-25" },
  ]);
});
